import React, { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ReactMetrics, datadogReporter } from '@cvent/sli-react-metrics';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getAnonymousId, getDatadogLogsConfig, getEnvironmentName } from './datadogConfig';
import { BUSINESS_UNIT, DD_GUEST_SERVICE_NAME, DD_HOST_SERVICE_NAME, PRODUCT } from './constants';
import Logger, { setEventOnGlobalLogContext, setUserOnGlobalLogContext } from './Logger';
import { setAnalyticsGlobalProperties } from './initAnalytics';

const LOG = new Logger('MetricsProvider');

export const initializeRUM = () => {
    if (typeof window !== 'undefined' && !datadogRum.getInitConfiguration()) {
        const hostName = window.location.hostname;
        const isHostApp = hostName.startsWith('app.');
        const datadogOptions = getDatadogLogsConfig();
        const { applicationId, clientToken, service, env, version, tracingOrigin, excludedActivityUrlsList } =
            datadogOptions;
        if (applicationId && clientToken) {
            // Initialize RUM
            datadogRum.init({
                applicationId,
                clientToken,
                service,
                env,
                version,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                traceSampleRate: 25,
                allowedTracingUrls: [tracingOrigin || window.location.origin],
                sessionSampleRate: isHostApp ? 100 : 4,
                sessionReplaySampleRate: 25,
                defaultPrivacyLevel: 'mask-user-input',
                trackSessionAcrossSubdomains: true,
                useSecureSessionCookie: true,
                useCrossSiteSessionCookie: true,
                trackingConsent: 'granted',
                usePartitionedCrossSiteSessionCookie: true,
                excludedActivityUrls: excludedActivityUrlsList,
            });
            LOG.debug('Initialized RUM');

            const secGpc = typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
            datadogRum.setGlobalContextProperty('sec-gpc', secGpc);
        }
    }
};

let environmentName = null;
let baseProps = {};

if (typeof window !== 'undefined') {
    environmentName = getEnvironmentName()?.environmentName;
    const userInfo = getBootstrap().get('user', {});
    // eslint-disable-next-line camelcase
    const { id: userId, email, organization_id } = userInfo;
    // DD userId should be unique
    let ddUserId = userId ? `splash-${environmentName}-user-${userId}` : undefined;
    if (!ddUserId) {
        // Fot guest side we set an anonymous ID for datadog
        const anonymousId = getAnonymousId();
        ddUserId = `splash-${environmentName}-anonymousUser-${anonymousId}`;
    }
    setUserOnGlobalLogContext(userId, email, organization_id, ddUserId);
    LOG.debug('Updated user in DD logs >', ddUserId);

    const eventInfo = getBootstrap().get('event', {});
    const { id: eventId, title: eventTitle } = eventInfo;
    setEventOnGlobalLogContext(eventId, eventTitle);
    LOG.debug('Updated event in DD logs >', eventId);
    setAnalyticsGlobalProperties({ eventId, eventTitle, organizationId: organization_id });

    const hostName = window.location.hostname;
    const isHostApp = hostName.startsWith('app.');
    // basic props from backstage + any custom props
    baseProps = {
        product: PRODUCT, // from backstage
        businessUnit: BUSINESS_UNIT,
        component: isHostApp ? DD_HOST_SERVICE_NAME : DD_GUEST_SERVICE_NAME,
        persona: isHostApp ? 'planner' : 'guest',
        cvent_duration: 0, // We need this for Datadog FRONTEND DURATION value
        user: { id: ddUserId, email, splashUserId: userId, organizationId: organization_id },
        event: { id: eventId, title: eventTitle },
    };
}

const MetricsProvider = ({ children }) => {
    const metricsEnabled = !__DEVELOPMENT__ && !__TESTING__;

    useEffect(() => {
        initializeRUM();
    }, []);

    return (
        <ReactMetrics
            config={{ environmentName, metricsEnabled, skipDeprecatedDefaultPrefix: true }}
            baseData={baseProps}
            reporters={[datadogReporter]}
        >
            {children}
        </ReactMetrics>
    );
};

export default MetricsProvider;
